import type { InteractionTopicInput } from "../queries";
import { OTHER_TOPIC } from "../queries";

export type TopicValidationErrors = {
  topicErrors: Record<number, string | undefined>;
  subtopicErrors: Record<number, string | undefined>;
  formError?: string;
};

export const OTHER_TOPIC_WARNING = `"${OTHER_TOPIC}" is always shown — remove this row.`;
export const OTHER_SUBTOPIC_WARNING = `"${OTHER_TOPIC}" is added automatically — remove this line.`;
export const MIN_TOPICS_ERROR = `Add at least one topic — "${OTHER_TOPIC}" alone is not enough.`;

export function validateInteractionTopics(topics: InteractionTopicInput[]): TopicValidationErrors {
  const topicErrors: Record<number, string | undefined> = {};
  const subtopicErrors: Record<number, string | undefined> = {};

  const seenTopics = new Map<string, number>();
  topics.forEach((topic, topicIndex) => {
    const name = topic.name.trim();
    if (!name) {
      topicErrors[topicIndex] = "Topic name is required.";
    } else if (name.toLowerCase() === OTHER_TOPIC.toLowerCase() && !isTopicLocked(topics, topicIndex)) {
      // A row renamed to Other would silently dedupe away on save — warn to
      // remove it instead of showing a confusing duplicate error.
      topicErrors[topicIndex] = OTHER_TOPIC_WARNING;
    } else {
      const key = name.toLowerCase();
      if (seenTopics.has(key)) {
        topicErrors[topicIndex] = "Duplicate topic.";
        const firstIndex = seenTopics.get(key);
        if (firstIndex !== undefined && !topicErrors[firstIndex]) {
          topicErrors[firstIndex] = "Duplicate topic.";
        }
      } else {
        seenTopics.set(key, topicIndex);
      }
    }

    // Subtopics edit as one textarea (one per line). Blank lines are ignored
    // (and stripped at save); only the first duplicate is reported. The
    // canonical trailing Other is a save-time concern (the backend appends
    // it), so a typed Other warns here instead of duplicating.
    const seenSubtopics = new Set<string>();
    for (const subtopic of topic.subtopics) {
      const subName = subtopic.trim();
      if (!subName) continue;
      const lower = subName.toLowerCase();
      if (lower === OTHER_TOPIC.toLowerCase()) {
        subtopicErrors[topicIndex] = OTHER_SUBTOPIC_WARNING;
        break;
      }
      if (seenSubtopics.has(lower)) {
        subtopicErrors[topicIndex] = `Duplicate subtopic "${subName}".`;
        break;
      }
      seenSubtopics.add(lower);
    }
  });

  const errors: TopicValidationErrors = { topicErrors, subtopicErrors };
  const realTopics = topics.filter(
    (topic, topicIndex) => !isTopicLocked(topics, topicIndex) && topic.name.trim().length > 0
  );
  if (realTopics.length === 0) {
    errors.formError = MIN_TOPICS_ERROR;
  }

  return errors;
}

export function hasValidationErrors(errors: TopicValidationErrors): boolean {
  return (
    Boolean(errors.formError) ||
    Object.values(errors.topicErrors).some(Boolean) ||
    Object.values(errors.subtopicErrors).some(Boolean)
  );
}

function normalizedSubtopics(lines: string[]): string[] {
  return lines.map((line) => line.trim()).filter((line) => line.length > 0);
}

function subtopicsTextEqual(a: string[], b: string[]): boolean {
  const aSubs = normalizedSubtopics(a);
  const bSubs = normalizedSubtopics(b);
  return aSubs.length === bSubs.length && aSubs.every((sub, subIndex) => sub === bSubs[subIndex]);
}

export function topicsEqual(a: InteractionTopicInput[], b: InteractionTopicInput[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((topic, index) => {
    const other = b[index];
    if (
      topic.name.trim() !== other.name.trim() ||
      (topic.code ?? null) !== (other.code ?? null) ||
      (topic.defaultPrivate ?? null) !== (other.defaultPrivate ?? null)
    ) {
      return false;
    }
    return subtopicsTextEqual(topic.subtopics, other.subtopics);
  });
}

export type TopicFieldChanges = Record<number, { name: boolean; subtopics: boolean }>;

// Diffs a sent save payload against the pre-save snapshot, keyed by the
// payload's indexes, so save confirmations can land on exactly the fields
// that changed. Names pair case-insensitively (both sides passed validation,
// so names are unique); a new name pairs with the same-index row when that
// old name vanished (a rename) so unchanged subtopic lines don't misflag.
// The same-index fallback only runs when the list length is unchanged — a
// delete in the same save shifts indexes, so the new name is treated as an
// add instead (the delete toast covers the removal).
export function diffSavedTopics(before: InteractionTopicInput[], after: InteractionTopicInput[]): TopicFieldChanges {
  const beforeByName = new Map(before.map((topic) => [topic.name.trim().toLowerCase(), topic]));
  const afterNames = new Set(after.map((topic) => topic.name.trim().toLowerCase()));
  const changes: TopicFieldChanges = {};
  after.forEach((topic, index) => {
    const key = topic.name.trim().toLowerCase();
    let prev = beforeByName.get(key);
    if (!prev && before.length === after.length) {
      const candidate = before[index];
      if (candidate && !afterNames.has(candidate.name.trim().toLowerCase())) {
        prev = candidate;
      }
    }
    if (!prev) {
      changes[index] = {
        name: true,
        subtopics: normalizedSubtopics(topic.subtopics).length > 0
      };
      return;
    }
    const nameChanged = prev.name.trim() !== topic.name.trim();
    const subtopicsChanged = !subtopicsTextEqual(prev.subtopics, topic.subtopics);
    if (nameChanged || subtopicsChanged) changes[index] = { name: nameChanged, subtopics: subtopicsChanged };
  });
  return changes;
}

// Merges a server echo into local edit state without dropping in-progress
// whitespace: when the echo matches local content modulo trimming and blank
// lines, local raw text wins (so a blank line being typed survives the echo
// that strips it) while server-owned metadata is still absorbed (otherwise a
// derived flag would read dirty forever and resave in a loop). Anything
// structurally different takes the echo whole.
export function mergeEchoTopics(
  localTopics: InteractionTopicInput[],
  echoTopics: InteractionTopicInput[]
): InteractionTopicInput[] {
  if (localTopics.length !== echoTopics.length) return echoTopics;
  const allMatch = localTopics.every((topic, index) => topicTextEqual(topic, echoTopics[index]));
  if (!allMatch) return echoTopics;
  return echoTopics.map((echo, index) => ({
    ...echo,
    name: localTopics[index].name,
    subtopics: localTopics[index].subtopics
  }));
}

function topicTextEqual(a: InteractionTopicInput, b: InteractionTopicInput): boolean {
  if (a.name.trim() !== b.name.trim()) return false;
  const aSubs = a.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
  const bSubs = b.subtopics.map((line) => line.trim()).filter((line) => line.length > 0);
  return aSubs.length === bSubs.length && aSubs.every((sub, subIndex) => sub === bSubs[subIndex]);
}

export function isOtherTopicName(name: string): boolean {
  return name.trim() === OTHER_TOPIC;
}

// The canonical Other row is always last (the editor inserts before it and the
// backend moves it last). Locking only the last Other keeps a row renamed to
// Other editable so the author can fix it instead of freezing.
export function isTopicLocked(topics: InteractionTopicInput[], topicIndex: number): boolean {
  return topicIndex === topics.length - 1 && isOtherTopicName(topics[topicIndex]?.name ?? "");
}
